import { supabase } from '../lib/supabase';
import { House } from '../types/models';

export const getHouses = async (): Promise<House[]> => {
  const { data, error } = await supabase
    .from('houses')
    .select('*')
    .order('house_name');
    
  if (error) {
    throw new Error(error.message);
  }
  
  return data || [];
};

export const getHouseById = async (id: string): Promise<House | null> => {
  const { data, error } = await supabase
    .from('houses')
    .select('*')
    .eq('house_id', id)
    .single();
    
  if (error) {
    throw new Error(error.message);
  }
  
  return data;
};

export const createHouse = async (house: Omit<House, 'house_id' | 'created_at'>): Promise<House> => {
  // Check if user has admin or manager role
  const { data: userRoles, error: rolesError } = await supabase
    .from('user_roles')
    .select(`
      roles!inner(
        role_name
      )
    `)
    .eq('user_id', (await supabase.auth.getUser()).data.user?.id);

  if (rolesError) {
    throw new Error('Failed to verify permissions');
  }

  const hasRequiredRole = userRoles?.some(ur => 
    ur.roles.role_name === 'admin' || ur.roles.role_name === 'manager'
  );

  if (!hasRequiredRole) {
    throw new Error('Unauthorized: Only administrators and managers can create houses');
  }

  const { data, error } = await supabase
    .from('houses')
    .insert([house])
    .select()
    .single();
    
  if (error) {
    throw new Error(error.message);
  }
  
  return data;
};

export const updateHouse = async (id: string, house: Partial<Omit<House, 'house_id' | 'created_at'>>): Promise<House> => {
  // Check if user has admin or manager role
  const { data: userRoles, error: rolesError } = await supabase
    .from('user_roles')
    .select(`
      roles!inner(
        role_name
      )
    `)
    .eq('user_id', (await supabase.auth.getUser()).data.user?.id);

  if (rolesError) {
    throw new Error('Failed to verify permissions');
  }

  const hasRequiredRole = userRoles?.some(ur => 
    ur.roles.role_name === 'admin' || ur.roles.role_name === 'manager'
  );

  if (!hasRequiredRole) {
    throw new Error('Unauthorized: Only administrators and managers can update houses');
  }

  const { data, error } = await supabase
    .from('houses')
    .update(house)
    .eq('house_id', id)
    .select()
    .single();
    
  if (error) {
    throw new Error(error.message);
  }
  
  return data;
};

export const deleteHouse = async (id: string): Promise<void> => {
  // Check if user has admin or manager role
  const { data: userRoles, error: rolesError } = await supabase
    .from('user_roles')
    .select(`
      roles!inner(
        role_name
      )
    `)
    .eq('user_id', (await supabase.auth.getUser()).data.user?.id);

  if (rolesError) {
    throw new Error('Failed to verify permissions');
  }

  const hasRequiredRole = userRoles?.some(ur => 
    ur.roles.role_name === 'admin' || ur.roles.role_name === 'manager'
  );

  if (!hasRequiredRole) {
    throw new Error('Unauthorized: Only administrators and managers can delete houses');
  }

  const { error } = await supabase
    .from('houses')
    .delete()
    .eq('house_id', id);
    
  if (error) {
    throw new Error(error.message);
  }
};

export const checkUserCanManageHouses = async (): Promise<boolean> => {
  const { data: userRoles } = await supabase
    .from('user_roles')
    .select(`
      roles!inner(
        role_name
      )
    `)
    .eq('user_id', (await supabase.auth.getUser()).data.user?.id);

  return userRoles?.some(ur => 
    ur.roles.role_name === 'admin' || ur.roles.role_name === 'manager'
  ) ?? false;
};